// Admin → Envíos → Impresión de etiquetas.
//
// Universo: envíos candidatos a etiqueta propia Lynx (tracking IS NULL
// en el server -- nunca los que vienen de Mercado Libre, que ya traen
// su propia etiqueta/QR). LynxPack queda afuera por construcción: no
// escribe en esta tabla. No se reimplementa ningún generador de
// etiqueta -- todo sale de labelService vía el endpoint existente.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import { useAuth } from "../context/AuthContext";
import { authFetch } from "../lib/authFetch";
import {
  LABEL_PRINT_FORMATS,
  LabelPrintFormat,
  printDeliveryLabelsBatch,
} from "../lib/printLabelsBatch";

type LabelRow = {
  id: number;
  lynx_code: string | null;
  store_id: number | null;
  nombre_fantasia: string | null;
  nombre_destinatario: string | null;
  direccion: string | null;
  localidad: string | null;
  codigo_postal: string | null;
  postal_code_norm: string | null;
  status: string | null;
  fecha_wynflex: string | null;
  label_printed_at: string | null;
};

type StoreOption = { id: number; nombre_fantasia: string };

type PrintedFilter = "all" | "true" | "false";

type Filters = {
  dateFrom: string;
  dateTo: string;
  storeId: string;
  printed: PrintedFilter;
  qCode: string;
  qDestinatario: string;
  qCp: string;
  qEstado: string;
};

const EMPTY_FILTERS: Filters = {
  dateFrom: "",
  dateTo: "",
  storeId: "",
  printed: "all",
  qCode: "",
  qDestinatario: "",
  qCp: "",
  qEstado: "",
};

const pageWrapStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "20px",
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "28px",
  fontWeight: 800,
  color: "var(--text-primary)",
  letterSpacing: "-0.02em",
};

const filtersGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
  gap: "14px",
};

const labelTextStyle: React.CSSProperties = {
  display: "block",
  fontSize: "12px",
  fontWeight: 700,
  marginBottom: "8px",
  color: "var(--text-secondary)",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
};

const selectStyle: React.CSSProperties = {
  width: "100%",
  minHeight: "46px",
  padding: "0 14px",
  borderRadius: "16px",
  border: "1px solid var(--border)",
  outline: "none",
  background: "var(--surface)",
  color: "var(--text-primary)",
  fontSize: "14px",
};

const tableWrapStyle: React.CSSProperties = {
  overflowX: "auto",
  border: "1px solid var(--border)",
  borderRadius: "18px",
  background: "var(--surface)",
};

const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "separate",
  borderSpacing: 0,
};

const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "12px 10px",
  color: "var(--text-secondary)",
  fontSize: "12px",
  fontWeight: 700,
  borderBottom: "1px solid var(--border)",
  background: "var(--surface-alt)",
  whiteSpace: "nowrap",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
};

const tdStyle: React.CSSProperties = {
  padding: "10px 10px",
  color: "var(--text-primary)",
  fontSize: "14px",
  borderBottom: "1px solid var(--border)",
  verticalAlign: "middle",
  whiteSpace: "nowrap",
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "block", width: "100%" }}>
      <span style={labelTextStyle}>{label}</span>
      {children}
    </label>
  );
}

function buildQuery(filters: Filters): string {
  const params = new URLSearchParams();
  if (filters.dateFrom) params.set("date_from", filters.dateFrom);
  if (filters.dateTo) params.set("date_to", filters.dateTo);
  if (filters.storeId) params.set("store_id", filters.storeId);
  if (filters.printed !== "all") params.set("printed", filters.printed);
  if (filters.qCode.trim()) params.set("q_code", filters.qCode.trim());
  if (filters.qDestinatario.trim()) params.set("q_destinatario", filters.qDestinatario.trim());
  if (filters.qCp.trim()) params.set("q_cp", filters.qCp.trim());
  if (filters.qEstado.trim()) params.set("q_estado", filters.qEstado.trim());
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

function formatFecha(value: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("es-AR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
}

const EtiquetasImpresion = () => {
  const { token, rol, userId } = useAuth();

  const [draftFilters, setDraftFilters] = useState<Filters>(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<Filters>(EMPTY_FILTERS);

  const [stores, setStores] = useState<StoreOption[]>([]);
  const [rows, setRows] = useState<LabelRow[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [format, setFormat] = useState<LabelPrintFormat>("a4");

  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // El universo de etiquetas (GET/POST /v2/deliveries/labels*) ya lo
  // limita el backend por Store cuando el rol es SELLER (ver
  // sellerStoreScope() en delivery.js) -- este fetch solo decide de DÓNDE
  // sale la lista para el selector de filtro, nunca es la seguridad real.
  // Admin: todas las Stores (GET /v2/stores, como siempre). Seller: solo
  // las que tiene vinculadas (mismo endpoint que ya usa SellerDeliveryCreate.tsx).
  useEffect(() => {
    const loadStores = async () => {
      try {
        if (rol === "SELLER") {
          if (!userId) return setStores([]);
          const roleRes = await authFetch(`/v2/users/${userId}/role`, { token });
          const rolePayload = await roleRes.json().catch(() => ({} as any));
          const sellerId = rolePayload?.data?.id;
          if (!sellerId) return setStores([]);
          const storesRes = await authFetch(`/v2/sellers/${sellerId}/store`, { token });
          const j = await storesRes.json().catch(() => ({} as any));
          const list = (j?.stores ?? j?.data ?? []) as any[];
          setStores(
            list
              .map((s) => ({ id: Number(s.id), nombre_fantasia: String(s.nombre_fantasia ?? s.name ?? `Store ${s.id}`) }))
              .filter((s) => Number.isInteger(s.id))
          );
          return;
        }

        const r = await authFetch("/v2/stores", { token });
        const j = await r.json();
        const list = (j?.stores ?? j?.data ?? []) as any[];
        setStores(
          list
            .map((s) => ({ id: Number(s.id), nombre_fantasia: String(s.nombre_fantasia ?? s.name ?? `Store ${s.id}`) }))
            .filter((s) => Number.isInteger(s.id))
        );
      } catch {
        setStores([]);
      }
    };
    loadStores();
  }, [token, rol, userId]);

  const loadLabels = useCallback(async (filters: Filters) => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(`/v2/deliveries/labels${buildQuery(filters)}`, { token });
      const json = await res.json().catch(() => ({} as any));
      if (!res.ok || json?.ok === false) {
        throw new Error(json?.error || `Error ${res.status}`);
      }
      setRows(Array.isArray(json.deliveries) ? json.deliveries : []);
      setSelectedIds(new Set());
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo cargar el listado");
      setRows([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    loadLabels(EMPTY_FILTERS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = () => {
    setAppliedFilters(draftFilters);
    loadLabels(draftFilters);
  };

  const clearFilters = () => {
    setDraftFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    loadLabels(EMPTY_FILTERS);
  };

  const allVisibleSelected = rows.length > 0 && rows.every((r) => selectedIds.has(r.id));

  const toggleAllVisible = () => {
    setSelectedIds((prev) => {
      if (allVisibleSelected) return new Set();
      const next = new Set(prev);
      rows.forEach((r) => next.add(r.id));
      return next;
    });
  };

  const toggleRow = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedCount = selectedIds.size;

  const handlePrint = async () => {
    if (!selectedCount) return;
    setPrinting(true);
    setError(null);
    setNotice(null);
    try {
      const result = await printDeliveryLabelsBatch(Array.from(selectedIds), format, { token });
      setNotice(
        result.skipped > 0
          ? `Se imprimieron ${result.labels.length} etiquetas (${result.skipped} seleccionadas no eran válidas para etiqueta Lynx).`
          : `Se imprimieron ${result.labels.length} etiquetas.`
      );
      await loadLabels(appliedFilters);
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo generar el lote de impresión");
    } finally {
      setPrinting(false);
    }
  };

  const storeOptions = useMemo(
    () => [...stores].sort((a, b) => a.nombre_fantasia.localeCompare(b.nombre_fantasia)),
    [stores]
  );

  return (
    <div style={pageWrapStyle}>
      <h1 style={titleStyle}>Impresión de etiquetas</h1>

      <Card>
        <div style={filtersGridStyle}>
          <Field label="Fecha desde">
            <input
              type="date"
              value={draftFilters.dateFrom}
              onChange={(e) => setDraftFilters((f) => ({ ...f, dateFrom: e.target.value }))}
              style={selectStyle}
            />
          </Field>
          <Field label="Fecha hasta">
            <input
              type="date"
              value={draftFilters.dateTo}
              onChange={(e) => setDraftFilters((f) => ({ ...f, dateTo: e.target.value }))}
              style={selectStyle}
            />
          </Field>
          {rol === "SELLER" && stores.length <= 1 ? null : (
            <Field label="Store">
              <select
                value={draftFilters.storeId}
                onChange={(e) => setDraftFilters((f) => ({ ...f, storeId: e.target.value }))}
                style={selectStyle}
              >
                <option value="">Todas</option>
                {storeOptions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nombre_fantasia}
                  </option>
                ))}
              </select>
            </Field>
          )}
          <Field label="Impreso">
            <select
              value={draftFilters.printed}
              onChange={(e) => setDraftFilters((f) => ({ ...f, printed: e.target.value as PrintedFilter }))}
              style={selectStyle}
            >
              <option value="all">Todos</option>
              <option value="false">No</option>
              <option value="true">Sí</option>
            </select>
          </Field>

          <Field label="Código / Tracking">
            <Input
              value={draftFilters.qCode}
              onChange={(e) => setDraftFilters((f) => ({ ...f, qCode: e.target.value }))}
              placeholder="LP00001234 / tracking"
            />
          </Field>
          <Field label="Destinatario">
            <Input
              value={draftFilters.qDestinatario}
              onChange={(e) => setDraftFilters((f) => ({ ...f, qDestinatario: e.target.value }))}
              placeholder="Nombre"
            />
          </Field>
          <Field label="Código postal">
            <Input
              value={draftFilters.qCp}
              onChange={(e) => setDraftFilters((f) => ({ ...f, qCp: e.target.value }))}
              placeholder="1425"
            />
          </Field>
          <Field label="Estado">
            <Input
              value={draftFilters.qEstado}
              onChange={(e) => setDraftFilters((f) => ({ ...f, qEstado: e.target.value }))}
              placeholder="Pendiente / En camino / ..."
            />
          </Field>
        </div>

        <div style={{ display: "flex", gap: "12px", marginTop: "16px" }}>
          <Button onClick={applyFilters} disabled={loading}>
            Filtrar
          </Button>
          <Button variant="secondary" onClick={clearFilters} disabled={loading}>
            Limpiar filtros
          </Button>
        </div>
      </Card>

      {error ? (
        <Card style={{ borderColor: "var(--danger)" }}>
          <span style={{ color: "var(--danger)" }}>{error}</span>
        </Card>
      ) : null}
      {notice ? (
        <Card>
          <span style={{ color: "var(--text-primary)" }}>{notice}</span>
        </Card>
      ) : null}

      <Card padding="0">
        <div style={tableWrapStyle}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>
                  <input type="checkbox" checked={allVisibleSelected} onChange={toggleAllVisible} />
                </th>
                <th style={thStyle}>Código</th>
                <th style={thStyle}>Fecha</th>
                <th style={thStyle}>Store</th>
                <th style={thStyle}>Destinatario</th>
                <th style={thStyle}>Dirección</th>
                <th style={thStyle}>Localidad</th>
                <th style={thStyle}>CP</th>
                <th style={thStyle}>Estado</th>
                <th style={thStyle}>Impreso</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td style={tdStyle} colSpan={10}>
                    Cargando...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td style={tdStyle} colSpan={10}>
                    Sin resultados para estos filtros.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id}>
                    <td style={tdStyle}>
                      <input type="checkbox" checked={selectedIds.has(row.id)} onChange={() => toggleRow(row.id)} />
                    </td>
                    <td style={tdStyle}>{row.lynx_code ?? "Pendiente"}</td>
                    <td style={tdStyle}>{formatFecha(row.fecha_wynflex)}</td>
                    <td style={tdStyle}>{row.nombre_fantasia ?? "—"}</td>
                    <td style={tdStyle}>{row.nombre_destinatario ?? "—"}</td>
                    <td style={tdStyle}>{row.direccion ?? "—"}</td>
                    <td style={tdStyle}>{row.localidad ?? "—"}</td>
                    <td style={tdStyle}>{row.postal_code_norm ?? row.codigo_postal ?? "—"}</td>
                    <td style={tdStyle}>{row.status ?? "—"}</td>
                    <td style={tdStyle}>
                      <span
                        style={{
                          padding: "3px 10px",
                          borderRadius: "999px",
                          fontSize: "12px",
                          fontWeight: 700,
                          color: row.label_printed_at ? "var(--success, #16a34a)" : "var(--text-secondary)",
                          border: `1px solid ${row.label_printed_at ? "var(--success, #16a34a)" : "var(--border)"}`,
                        }}
                      >
                        {row.label_printed_at ? "Sí" : "No"}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Card>
        <div style={{ display: "flex", alignItems: "center", gap: "20px", flexWrap: "wrap" }}>
          <span style={{ fontWeight: 700, color: "var(--text-primary)" }}>Seleccionados: {selectedCount}</span>

          <div style={{ minWidth: "180px" }}>
            <select value={format} onChange={(e) => setFormat(e.target.value as LabelPrintFormat)} style={selectStyle}>
              {LABEL_PRINT_FORMATS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
          </div>

          <Button onClick={handlePrint} disabled={!selectedCount || printing}>
            {printing ? "Generando..." : "Imprimir seleccionadas"}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default EtiquetasImpresion;
